import React, { useState, useEffect, useRef } from 'react';
import { FaBriefcase, FaPlus, FaEdit, FaTrash, FaSave, FaTimes, FaSearch, FaBook, FaGraduationCap, FaChevronDown } from 'react-icons/fa';
import Toast from '../components/Toast';
import * as masterService from '../services/masterService';

const CreateSpecialization = () => {
  const [specializations, setSpecializations] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [streams, setStreams] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    subjectId: '',
    streamId: ''
  });
  const [editingId, setEditingId] = useState(null);
  const [toast, setToast] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Search & Dropdown State
  const [searchSubject, setSearchSubject] = useState('');
  const [showSubjectDropdown, setShowSubjectDropdown] = useState(false);
  const [searchStream, setSearchStream] = useState('');
  const [showStreamDropdown, setShowStreamDropdown] = useState(false);

  const subjectRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (subjectRef.current && !subjectRef.current.contains(event.target)) {
        setShowSubjectDropdown(false);
      }
      if (streamRef.current && !streamRef.current.contains(event.target)) {
        setShowStreamDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      setIsLoading(true);
      const [specsData, subjectsData, streamsData] = await Promise.all([
        masterService.getAllSpecializations(),
        masterService.getAllSubjects(),
        masterService.getAllStreams()
      ]);

      if (specsData && specsData.success && Array.isArray(specsData.data)) {
        setSpecializations(specsData.data);
      } else if (Array.isArray(specsData)) {
        setSpecializations(specsData);
      } else {
        setSpecializations([]);
      }

      if (subjectsData && subjectsData.success && Array.isArray(subjectsData.data)) {
        setSubjects(subjectsData.data);
      } else if (Array.isArray(subjectsData)) {
        setSubjects(subjectsData);
      } else {
        setSubjects([]);
      }

      if (streamsData && streamsData.success && Array.isArray(streamsData.data)) {
        setStreams(streamsData.data);
      } else if (Array.isArray(streamsData)) {
        setStreams(streamsData);
      } else {
        setStreams([]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      showToast(error.message || 'Failed to fetch data', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSpecializations = async () => {
    try {
      const response = await masterService.getAllSpecializations();
      if (response && response.success && Array.isArray(response.data)) {
        setSpecializations(response.data);
      } else if (Array.isArray(response)) {
        setSpecializations(response);
      } else {
        setSpecializations([]);
      }
    } catch (error) {
      console.error('Error fetching specializations:', error);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'subjectId') {
      setFormData(prev => ({
        ...prev,
        subjectId: value,
        streamId: ''
      }));
      setSearchStream('');
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.subjectId || !formData.streamId) {
      showToast('Please fill in all required fields', 'error');
      return;
    }

    const payload = {
      name: formData.name,
      stream_id: formData.streamId,
      subject_id: formData.subjectId // Optional but good for redundancy if backend uses it
    };

    try {
      setIsLoading(true);
      if (editingId) {
        await masterService.updateSpecialization(editingId, payload);
        showToast('Specialization updated successfully', 'success');
        setEditingId(null);
      } else {
        await masterService.createSpecialization(payload);
        showToast('Specialization created successfully', 'success');
      }

      setFormData({ name: '', subjectId: '', streamId: '' });
      setSearchSubject('');
      setSearchStream('');
      await fetchSpecializations();

    } catch (error) {
      console.error('Error saving specialization:', error);
      showToast(error.message || 'Failed to save specialization', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (spec) => {
    let streamId = spec.stream_id || (spec.stream ? spec.stream.id : '') || '';
    let subjectId = spec.subject_id || (spec.subject ? spec.subject.id : '') || '';

    if (!subjectId && streamId) {
      const stream = streams.find(s => s.id === streamId || s._id === streamId);
      if (stream) {
        subjectId = stream.subject_id || (stream.subject ? stream.subject.id : '');
      }
    }

    setFormData({
      name: spec.name,
      subjectId: subjectId,
      streamId: streamId
    });

    const sName = getSubjectName(subjectId);
    setSearchSubject(sName === '-' ? '' : sName);

    const streamName = getStreamName(streamId);
    setSearchStream(streamName === '-' ? '' : streamName);

    setEditingId(spec.id);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this specialization?')) {
      try {
        setIsLoading(true);
        await masterService.deleteSpecialization(id);
        showToast('Specialization deleted successfully', 'success');
        await fetchSpecializations();
      } catch (error) {
        console.error('Error deleting specialization:', error);
        showToast(error.message || 'Failed to delete specialization', 'error');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setFormData({
      name: '',
      subjectId: '',
      streamId: ''
    });
    setSearchSubject('');
    setSearchStream('');
  };

  const getFilteredStreamsForDropdown = () => {
    if (!formData.subjectId) return [];
    return streams.filter(s => {
      const sSubjectId = s.subject_id || (s.subject ? s.subject.id : '');
      const matchesSubject = sSubjectId === formData.subjectId;
      const matchesSearch = s.name.toLowerCase().includes(searchStream.toLowerCase());
      return matchesSubject && matchesSearch;
    });
  };

  const filteredSubjects = subjects.filter(subject =>
    subject.name.toLowerCase().includes(searchSubject.toLowerCase())
  );

  const getSubjectName = (id) => {
    const subject = subjects.find(s => s.id === id || s._id === id);
    return subject ? subject.name : '-';
  };

  const getStreamName = (id) => {
    const stream = streams.find(s => s.id === id || s._id === id);
    return stream ? stream.name : '-';
  };

  const resolveSpecDetails = (spec) => {
    let streamId = spec.stream_id || (spec.stream ? spec.stream.id : '') || '';
    let subjectId = spec.subject_id || (spec.subject ? spec.subject.id : '') || '';

    if (!subjectId && streamId) {
      const stream = streams.find(s => s.id === streamId || s._id === streamId);
      if (stream) {
        subjectId = stream.subject_id || (stream.subject ? stream.subject.id : '');
      }
    }

    return {
      subjectName: getSubjectName(subjectId),
      streamName: getStreamName(streamId)
    };
  };

  const filteredSpecializations = specializations.filter(spec =>
    spec.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    resolveSpecDetails(spec).subjectName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    resolveSpecDetails(spec).streamName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const darkGradient = {
    background: 'linear-gradient(135deg, rgb(15, 23, 42) 0%, rgb(49, 46, 129) 50%, rgb(15, 23, 42) 100%)'
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-blue-50 py-8 px-4">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <div className="max-w mx-auto">
        {/* Header */}
        <div className="rounded-2xl shadow-xl p-6 mb-8" style={darkGradient}>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
              <FaBriefcase className="text-white text-2xl" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">Specialization Management</h1>
              <p className="text-blue-100 mt-1">Create and manage academic specializations with Department and Stream</p>
            </div>
          </div>
        </div>

        {/* Form Section */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <FaPlus className="text-indigo-500" />
            {editingId ? 'Edit Specialization' : 'Add New Specialization'}
          </h2>

          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4">
              {/* Department Selection */}
              <div ref={subjectRef}>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Department <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={searchSubject}
                    onChange={(e) => {
                      setSearchSubject(e.target.value);
                      setShowSubjectDropdown(true);
                      if (formData.subjectId && e.target.value !== getSubjectName(formData.subjectId)) {
                        // Keep ID until explicit selection or clear? 
                        // For now we just update search term.
                      }
                    }}
                    onFocus={() => setShowSubjectDropdown(true)}
                    placeholder="Search or select department..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all pr-10"
                    disabled={isLoading}
                  />
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-1 pointer-events-none">
                    <FaChevronDown className="text-gray-400" />
                  </div>

                  {showSubjectDropdown && filteredSubjects.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {filteredSubjects.map(subject => (
                        <button
                          key={subject.id}
                          type="button"
                          onClick={() => {
                            setFormData(prev => ({
                              ...prev,
                              subjectId: subject.id,
                              streamId: '' // Reset stream
                            }));
                            setSearchSubject(subject.name);
                            setSearchStream('');
                            setShowSubjectDropdown(false);
                          }}
                          className="w-full px-4 py-3 text-left hover:bg-indigo-50 border-b border-gray-100 last:border-b-0 flex items-center gap-2 transition-colors"
                        >
                          <FaBook className="text-indigo-500" />
                          <span className="text-gray-700 font-medium">{subject.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {formData.subjectId && (
                  <div className="mt-2 text-sm text-green-600 flex items-center gap-1">
                    <span>✓ Selected: {getSubjectName(formData.subjectId)}</span>
                  </div>
                )}
              </div>

              {/* Stream Selection */}
              <div ref={streamRef}>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Stream <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={searchStream}
                    onChange={(e) => {
                      setSearchStream(e.target.value);
                      setShowStreamDropdown(true);
                    }}
                    onFocus={() => setShowStreamDropdown(true)}
                    placeholder={!formData.subjectId ? "Select a department first" : "Search or select stream..."}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all pr-10"
                    disabled={isLoading || !formData.subjectId}
                  />
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-1 pointer-events-none">
                    <FaChevronDown className="text-gray-400" />
                  </div>

                  {showStreamDropdown && formData.subjectId && getFilteredStreamsForDropdown().length > 0 && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {getFilteredStreamsForDropdown().map(stream => (
                        <button
                          key={stream.id}
                          type="button"
                          onClick={() => {
                            setFormData(prev => ({ ...prev, streamId: stream.id }));
                            setSearchStream(stream.name);
                            setShowStreamDropdown(false);
                          }}
                          className="w-full px-4 py-3 text-left hover:bg-indigo-50 border-b border-gray-100 last:border-b-0 flex items-center gap-2 transition-colors"
                        >
                          <FaGraduationCap className="text-indigo-500" />
                          <span className="text-gray-700 font-medium">{stream.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {formData.streamId && (
                  <div className="mt-2 text-sm text-green-600 flex items-center gap-1">
                    <span>✓ Selected: {getStreamName(formData.streamId)}</span>
                  </div>
                )}
              </div>

              {/* Specialization Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Specialization Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g., Artificial Intelligence"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                  required
                  disabled={isLoading}
                  autoComplete="off"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3">
              {editingId && (
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  disabled={isLoading}
                  className="flex items-center gap-2 px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-300 transition-all shadow-md"
                >
                  <FaTimes />
                  Cancel
                </button>
              )}
              <button
                type="submit"
                disabled={isLoading}
                className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-indigo-500 to-blue-500 text-white rounded-xl font-semibold hover:from-indigo-600 hover:to-blue-600 transition-all shadow-lg hover:shadow-xl disabled:opacity-50"
              >
                {isLoading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                ) : (
                  <FaSave />
                )}
                {editingId ? 'Update Specialization' : 'Save Specialization'}
              </button>
            </div>
          </form>
        </div>

        {/* List Section */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
            <h2 className="text-2xl font-bold text-gray-900">
              All Specializations ({specializations.length})
            </h2>

            <div className="relative">
              <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search specializations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all w-full md:w-80"
              />
            </div>
          </div>

          {isLoading && specializations.length === 0 ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-500 border-t-transparent mx-auto mb-4"></div>
              <p className="text-gray-500 text-lg">Loading specializations...</p>
            </div>
          ) : filteredSpecializations.length === 0 ? (
            <div className="text-center py-12">
              <FaBriefcase className="text-6xl text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">
                {searchTerm ? 'No specializations found matching your search' : 'No specializations created yet'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr style={darkGradient}>
                    <th className="px-6 py-4 text-left text-sm font-bold text-white">S.No</th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-white">Department</th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-white">Stream</th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-white">Specialization</th>
                    <th className="px-6 py-4 text-center text-sm font-bold text-white">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredSpecializations.map((spec, index) => {
                    const details = resolveSpecDetails(spec);
                    return (
                      <tr key={spec.id} className="hover:bg-indigo-50/50 transition-colors">
                        <td className="px-6 py-4 text-sm text-gray-900">{index + 1}</td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-semibold">
                            {details.subjectName}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs font-semibold">
                            {details.streamName}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 font-medium">{spec.name}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => handleEdit(spec)}
                              className="p-2 bg-indigo-100 text-indigo-600 rounded-lg hover:bg-indigo-200 transition-all"
                              title="Edit"
                            >
                              <FaEdit />
                            </button>
                            <button
                              onClick={() => handleDelete(spec.id)}
                              className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-all"
                              title="Delete"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateSpecialization;